import {
  Button,
  type ColumnDef,
  ConfirmDialog,
  EmptyState,
  InlineMessage,
  ListView,
  PageContent,
  type SortState,
} from '@olytic/foundation';
import { Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { useDocumentTitle } from '../documentTitle';
import { type BinItem, type BinList, deleteEntries, emptyBin, listBin, restoreEntries } from './recycleBinApi';

/**
 * Setup → Recycle Bin: deleted records, with everything their delete took
 * with them, until they are restored, deleted for good, or purged after the
 * retention period (recycleBin.mjs).
 */

const DATE_TIME = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const when = (iso: string) => (iso ? DATE_TIME.format(new Date(iso)) : '');
const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong.');
const records = (n: number) => (n === 1 ? '1 record' : `${n} records`);

const COLUMNS: ColumnDef<BinItem>[] = [
  { key: 'name', label: 'Record Name', primary: true, sortable: true, get: (row) => row.name, href: () => null },
  { key: 'objectLabel', label: 'Object', sortable: true, get: (row) => row.objectLabel },
  { key: 'deletedBy', label: 'Deleted By', sortable: true, get: (row) => row.deletedBy, hideOnMobile: true },
  { key: 'deletedAt', label: 'Deleted At', sortable: true, nowrap: true, get: (row) => row.deletedAt, render: (row) => when(row.deletedAt) },
  { key: 'daysLeft', label: 'Days Left', sortable: true, align: 'right', get: (row) => row.daysLeft },
];

type Load = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; bin: BinList };
type Pending = { kind: 'delete'; ids: string[] } | { kind: 'empty' } | null;

export function RecycleBinPage(): JSX.Element {
  useDocumentTitle('Recycle Bin');
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | undefined>({ key: 'deletedAt', direction: 'desc' });
  const [selected, setSelected] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    listBin(authenticatedFetch).then(
      (bin) => live && setLoad({ state: 'ready', bin }),
      (error: unknown) => live && setLoad({ state: 'error', message: errorText(error) }),
    );
    return () => {
      live = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const bin = load.state === 'ready' ? load.bin : null;
  const nameOf = useMemo(() => new Map((bin?.items ?? []).map((i) => [i.id, i.name || i.objectLabel])), [bin]);

  const restore = useCallback(async (ids: string[]) => {
    setNotice(null);
    try {
      const { restored, failed } = await restoreEntries(authenticatedFetch, ids);
      const done = restored.length === 1 ? `Restored ${nameOf.get(restored[0]!.id) ?? '1 record'}.` : `Restored ${records(restored.length)}.`;
      setNotice(failed.length
        ? { tone: 'error', text: `${restored.length ? `${done} ` : ''}${nameOf.get(failed[0]!.id) ?? 'A record'} could not be restored: ${failed[0]!.error}${failed.length > 1 ? ` (${failed.length - 1} more could not either.)` : ''}` }
        : { tone: 'success', text: done });
    } catch (error) {
      setNotice({ tone: 'error', text: errorText(error) });
    }
    setSelected([]);
    reload();
  }, [nameOf, reload]);

  const confirm = useCallback(async () => {
    if (pending === null) return;
    setBusy(true);
    try {
      const deleted = pending.kind === 'empty'
        ? await emptyBin(authenticatedFetch)
        : await deleteEntries(authenticatedFetch, pending.ids);
      setNotice({ tone: 'success', text: `Permanently deleted ${records(deleted)}.` });
      setPending(null);
      setSelected([]);
      reload();
    } catch (error) {
      setNotice({ tone: 'error', text: errorText(error) });
      setPending(null);
    } finally {
      setBusy(false);
    }
  }, [pending, reload]);

  const days = bin?.retentionDays ?? 30;
  const deleteCount = pending?.kind === 'delete' ? pending.ids.length : bin?.items.length ?? 0;

  return (
    <PageContent as="section" width="fill">
      {notice ? <InlineMessage tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'} className="mb-3">{notice.text}</InlineMessage> : null}
      <ListView
        objectLabel="Recycle Bin"
        objectIcon={<Trash2 />}
        columns={COLUMNS}
        rows={bin?.items ?? []}
        getRowId={(row) => row.id}
        mode="client"
        search={search}
        onSearchChange={setSearch}
        {...(sort ? { sort } : {})}
        onSortChange={setSort}
        selectedIds={selected}
        onSelectionChange={setSelected}
        massActions={[
          { label: 'Restore', onSelect: (ids) => { void restore(ids); } },
          { label: 'Delete Permanently', destructive: true, onSelect: (ids) => setPending({ kind: 'delete', ids }) },
        ]}
        rowActions={[
          { label: 'Restore', onSelect: (row) => { void restore([row.id]); } },
          { label: 'Delete Permanently', destructive: true, onSelect: (row) => setPending({ kind: 'delete', ids: [row.id] }) },
        ]}
        headerActions={bin?.canEmpty ? (
          <Button variant="outline" size="md" disabled={bin.items.length === 0} onClick={() => setPending({ kind: 'empty' })}>
            Empty Recycle Bin
          </Button>
        ) : undefined}
        pagination="pages"
        onRefresh={reload}
        loading={load.state === 'loading'}
        {...(load.state === 'error' ? { error: load.message } : {})}
        empty={
          <EmptyState
            icon={<Trash2 />}
            title={search.trim() ? `Nothing in the Recycle Bin matches “${search.trim()}”` : 'The Recycle Bin is empty'}
            description={`Deleted records stay here for ${days} days, with everything deleted along with them, then are deleted for good.`}
          />
        }
      />
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => { if (!open && !busy) setPending(null); }}
        title={pending?.kind === 'empty' ? 'Empty the Recycle Bin?' : `Permanently delete ${records(deleteCount)}?`}
        description={pending?.kind === 'empty'
          ? `Every record in the Recycle Bin (${records(deleteCount)}) is deleted for good. It cannot be undone.`
          : `${deleteCount === 1 ? 'It is' : 'They are'} deleted for good, with everything deleted along with ${deleteCount === 1 ? 'it' : 'them'}. It cannot be undone.`}
        confirmLabel={pending?.kind === 'empty' ? 'Empty Recycle Bin' : 'Delete Permanently'}
        tone="danger"
        busy={busy}
        onConfirm={() => { void confirm(); }}
      />
    </PageContent>
  );
}
